import { SERVER_PARAM, SERVER_STORAGE_KEY, type ServerChoice } from './config';

// T-210 — moving between the backend and the in-memory demo on a build that has both: the remembered choice,
// the address a choice becomes, and the one place the app navigates to it.
//
// This is `app/dataset.ts` for the other axis, deliberately in the same shape, and for the same reason: the app
// has exactly ONE `ApiClient`, built once in `main.tsx` before React starts (ARCH §8.2, §9.1), and the whole
// query cache hangs off it. Swapping the transport under a running app would mean rebuilding all of it; a
// navigation rebuilds it honestly and costs one load of a static build. So choosing a server sets the address
// and reloads, and the address is the truth about what is on screen.
//
// **It is not a mode switch** (REQ-100 AC4, ARCH §1.2): what it chooses is which server answers, and each
// server states its own account's mode. The demo server has no network code at all, so nothing here can widen
// what reaches Meta — and on a DEMO build none of it applies, because `readAppConfig` does not consult the
// address or storage for a transport that build could not have.

/**
 * **Where a server switch lands, and why it is always Home** (T-432).
 *
 * The welcome screen's two calls to action and the mode menu's "Switch to my ad account" all go through
 * `goToServer`, and none of them may keep the route it was pressed on: `/entity/abc` on the demo server names
 * a campaign of the SAMPLE shop, and the live account has no such id — a switch that kept the path would land
 * a reader on a "we could not find that" for a thing that exists, on the other server. Home is the one route
 * every account has.
 *
 * It lives here rather than on the welcome page because the shell presses it too, and `app/shell/bundle.test.ts`
 * forbids the shell's first chunk from reaching a route module to read a constant out of it.
 */
export const HOME_PATH = '/home';

/** The remembered choice, or `null` when there is none (or storage is refused). */
export function readStoredServer(): string | null {
  try {
    // T-463 (D-1277): the choice lasts the VISIT — a reload keeps the demo you opened, a fresh visit to the box
    // opens the real account again. It used to be localStorage, and the box then showed the demo for days.
    return window.sessionStorage.getItem(SERVER_STORAGE_KEY);
  } catch {
    return null; // private mode: an API build opens on its backend, as it always did
  }
}

export function storeServer(choice: ServerChoice): void {
  try {
    window.sessionStorage.setItem(SERVER_STORAGE_KEY, choice);
  } catch {
    /* quota or private mode — the choice simply is not remembered */
  }
}

/**
 * The address a choice becomes: `?server=demo` in the SEARCH part (the backend is the default and takes no
 * parameter, so its address is the plain one), and the hash route either kept or replaced by `path`.
 *
 * Every other query parameter is dropped, as `datasetHref` drops them: the five QA switches belong to a demo
 * BUILD and this build is not one, so carrying `?demoFault=error` across the switch would arm a fault that
 * nothing here honours.
 */
export function serverHref(
  location: Pick<Location, 'origin' | 'pathname' | 'hash'>,
  choice: ServerChoice,
  path?: string,
): string {
  const search = choice === 'demo' ? `?${SERVER_PARAM}=demo` : '';
  const hash = path === undefined ? location.hash : `#${path}`;
  return `${location.origin}${location.pathname}${search}${hash}`;
}

/**
 * Go to a server: remember it, then load its address. `path` is the route to land on — "Try the demo" lands on
 * Home, as its link does on a demo build.
 *
 * `location.assign` lives behind this seam for the same reason `goToDataset` keeps it there: jsdom's `Location`
 * is not configurable, so a test that cannot watch the navigation cannot prove the switch went where it says.
 */
export function goToServer(choice: ServerChoice, path?: string): void {
  storeServer(choice);
  window.location.assign(serverHref(window.location, choice, path));
}
